import {
  useExternalSupplierShipmentsControllerListCouriers,
  useExternalSupplierShipmentsControllerListMyTargets,
  useExternalSupplierShipmentsControllerRegister,
  useExternalSupplierShipmentsControllerUpdateStatus,
  type CourierDto,
  type SupplierShipmentTargetDto,
} from "@semo/api-contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

/**
 * 몰 배송 — 공급사가 「보낼 것」을 보고 송장을 거는 화면의 데이터.
 *
 * 등록·상태변경 뒤에는 **목록을 다시 받는다.** 남은 수량과 주문 상태가 서버에서 다시
 * 계산되기 때문이다(다 보낸 주문은 목록에서 아예 빠진다) — 화면에서 흉내 내면 그 규칙이
 * 두 벌이 되고, 한쪽만 고쳐지는 날이 온다.
 */
export function useMallShipments() {
  const queryClient = useQueryClient();

  const targetsQuery = useExternalSupplierShipmentsControllerListMyTargets();
  const couriersQuery = useExternalSupplierShipmentsControllerListCouriers();

  const registerMutation = useExternalSupplierShipmentsControllerRegister();
  const statusMutation = useExternalSupplierShipmentsControllerUpdateStatus();

  const targets = useMemo<SupplierShipmentTargetDto[]>(
    () => targetsQuery.data?.data ?? [],
    [targetsQuery.data],
  );

  const couriers = useMemo<CourierDto[]>(
    () => couriersQuery.data?.data ?? [],
    [couriersQuery.data],
  );

  /** 목록 무효화 — 등록·상태변경이 끝나면 서버 판정을 다시 받는다. */
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries();
    await targetsQuery.refetch();
  }, [queryClient, targetsQuery]);

  return {
    targets,
    couriers,
    isLoading: targetsQuery.isLoading,
    error: targetsQuery.error,
    refetch: targetsQuery.refetch,
    register: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    updateStatus: statusMutation.mutateAsync,
    isUpdatingStatus: statusMutation.isPending,
    refresh,
  };
}
